import React, { useMemo, useRef, useState } from 'react';
import { Search, X, MessageSquare, BellDot, Trash2, UserPlus, Phone } from 'lucide-react';
import Avatar from './Avatar';
import { createCustomer } from '../../services/api';

export default function ChatList({ customers, selected, onSelect, onDelete, onCustomerCreated }) {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('all'); // 'all' | 'unread'
  const searchInputRef = useRef(null);
  const [listError, setListError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newName, setNewName] = useState('');
  const [addingCustomer, setAddingCustomer] = useState(false);

  const baseFiltered = useMemo(() => {
    const q = query.toLowerCase();
    return customers.filter(c =>
      !q ||
      c.customer_id.toLowerCase().includes(q) ||
      (c.name && c.name.toLowerCase().includes(q)) ||
      (c.last_message || '').toLowerCase().includes(q)
    );
  }, [customers, query]);

  const items = useMemo(() => {
    if (tab === 'unread') return baseFiltered.filter(c => (c.unread_count || 0) > 0);
    return baseFiltered;
  }, [baseFiltered, tab]);

  const unreadCount = useMemo(() => customers.reduce((acc, c) => acc + (c.unread_count || 0), 0), [customers]);

  const handleDelete = async (event, customer) => {
    if (typeof onDelete !== 'function') return;
    event.stopPropagation();
    const displayName = customer.name || customer.customer_id;
    const confirmed = window.confirm(`Delete conversation with ${displayName}? This removes all messages for this contact.`);
    if (!confirmed) return;

    setDeletingId(customer.customer_id);
    setListError('');
    try {
      await onDelete(customer.customer_id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let message = 'Failed to delete conversation.';
      if (detail) {
        if (typeof detail === 'string') message = detail;
        else if (typeof detail === 'object') {
          message = detail.message || detail.error || message;
        }
      } else if (err?.message) {
        message = err.message;
      }
      setListError(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    const phone = newPhone.trim();
    if (!phone) return;

    setAddingCustomer(true);
    setListError('');
    try {
      const { data } = await createCustomer(phone, newName.trim() || null);
      // Select the newly created customer
      onSelect(data.customer_id);
      // Reload customers list
      if (onCustomerCreated) onCustomerCreated();
      // Reset form
      setNewPhone('');
      setNewName('');
      setShowAddForm(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setListError(typeof detail === 'string' ? detail : 'Failed to add customer.');
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleQuickStart = async (phoneStr) => {
    const phone = phoneStr.replace(/[\s-]/g, '');
    if (!phone) return;
    
    setAddingCustomer(true);
    setListError('');
    try {
      const { data } = await createCustomer(phone, null);
      onSelect(data.customer_id);
      if (onCustomerCreated) onCustomerCreated();
      setQuery('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setListError(typeof detail === 'string' ? detail : 'Failed to start chat.');
    } finally {
      setAddingCustomer(false);
    }
  };

  const cleanQuery = query.replace(/[\s-]/g, '');
  const isPhoneNumber = /^\+?\d{8,16}$/.test(cleanQuery);

  return (
    <div className="chat-list">
      {/* Toolbar with tabs + add button */}
      <div className="list-toolbar">
        <div className="toolbar-top-row">
          <div className="tabs" role="tablist" aria-label="Conversation filters">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'all'}
              className={`tab ${tab === 'all' ? 'active' : ''}`}
              onClick={() => setTab('all')}
            >
              <MessageSquare size={16} className="tab-icon" aria-hidden="true" />
              <span className="tab-label">All</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'unread'}
              className={`tab ${tab === 'unread' ? 'active' : ''}`}
              onClick={() => setTab('unread')}
            >
              <BellDot size={16} className="tab-icon" aria-hidden="true" />
              <span className="tab-label">Unread</span>
              {unreadCount > 0 && (
                <span className="tab-count" aria-label={`${unreadCount} unread conversations`}>{unreadCount}</span>
              )}
            </button>
          </div>
          <button
            type="button"
            className="add-customer-btn"
            title="Add new customer"
            aria-label="Add new customer"
            onClick={() => setShowAddForm(v => !v)}
          >
            <UserPlus size={18} />
          </button>
        </div>
        <div className="toolbar-search-row">
          <div className="search-wrap" role="search">
            <button
              type="button"
              className="search-icon-btn"
              aria-label="Focus search"
              onClick={() => searchInputRef.current?.focus()}
            >
              <Search size={16} className="icon" aria-hidden="true" />
            </button>
            <input
              ref={searchInputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') setQuery('');
              }}
              placeholder="Search or start new chat"
              aria-label="Search conversations"
            />
            {query && (
              <button
                type="button"
                className="search-clear"
                aria-label="Clear search"
                onClick={() => {
                  setQuery('');
                  searchInputRef.current?.focus();
                }}
              >
                <X size={14} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Add Customer Form */}
      {showAddForm && (
        <form className="add-customer-form" onSubmit={handleAddCustomer}>
          <div className="add-customer-fields">
            <div className="add-field-row">
              <Phone size={16} className="field-icon" />
              <input
                type="text"
                placeholder="Phone number (e.g. 919876543210)"
                value={newPhone}
                onChange={e => setNewPhone(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="add-field-row">
              <UserPlus size={16} className="field-icon" />
              <input
                type="text"
                placeholder="Name (optional)"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
            </div>
          </div>
          <div className="add-customer-actions">
            <button type="submit" className="btn-add" disabled={addingCustomer || !newPhone.trim()}>
              {addingCustomer ? 'Adding...' : 'Add Customer'}
            </button>
            <button type="button" className="btn-cancel" onClick={() => { setShowAddForm(false); setNewPhone(''); setNewName(''); }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {listError && (
        <div className="chat-list-error" role="alert">
          {listError}
        </div>
      )}
      {items.map(c => (
        <div
          key={c.customer_id}
          onClick={() => { onSelect(c.customer_id); setQuery(''); }}
          className={`customer-list-item ${selected === c.customer_id ? 'selected' : ''}`}
        >
          <div className="avatar">
            <Avatar name={c.name || c.customer_id} size={40} />
          </div>
          <div className="customer-details">
            <div className="name-time">
              <div className="name">{c.name || c.customer_id}</div>
              <div className="time-actions">
                <div className="time">
                  {c.last_ts ? (() => {
                    try {
                      const date = new Date(c.last_ts);
                      if (isNaN(date.getTime())) return '';
                      return date.toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      });
                    } catch (error) {
                      return '';
                    }
                  })() : ''}
                </div>
                {typeof onDelete === 'function' && (
                  <button
                    type="button"
                    className="delete-conversation-btn"
                    title="Delete conversation"
                    aria-label={`Delete conversation with ${c.name || c.customer_id}`}
                    onClick={(event) => handleDelete(event, c)}
                    disabled={deletingId === c.customer_id}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
            <div className="message-preview-container">
              <div className="message-preview">{c.last_message}</div>
              {c.unread_count > 0 && (
                <div className="unread-badge" title="Unread messages">
                  {c.unread_count}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      {/* If query does not match any existing customer, and it IS a phone number, show start new chat option */}
      {query && isPhoneNumber && !customers.some(c => c.customer_id === cleanQuery || c.customer_id === query) && (
        <div
          onClick={() => handleQuickStart(cleanQuery)}
          className={`customer-list-item ${addingCustomer ? 'loading' : ''}`}
          style={{ opacity: addingCustomer ? 0.6 : 1, pointerEvents: addingCustomer ? 'none' : 'auto' }}
        >
          <div className="avatar">
            <Avatar name={cleanQuery} size={40} />
          </div>
          <div className="customer-details">
            <div className="name-time">
              <div className="name">{addingCustomer ? 'Starting chat...' : `Start chat with ${cleanQuery}`}</div>
            </div>
          </div>
        </div>
      )}
      {items.length === 0 && <div className="no-conversations">No conversations found</div>}
    </div>
  );
}